import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Clock,
  Download,
  Mail,
  CheckSquare,
  PenTool,
  MessageSquare,
  Target,
  Gamepad2,
  Cloud,
  Trophy,
  CheckCircle2,
  Edit3,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';

interface ActivitySubmission {
  id: string;
  type: 'quiz' | 'draw' | 'open' | 'matching' | 'game' | 'wordcloud' | 'poll';
  name: string;
  icon: any;
  completed: boolean;
  autoGraded: boolean;
  score: number | null;
  maxScore: number;
  timeSpent: number;
  submittedAt: Date;
  content?: any;
  teacherComment?: string;
  manualScore?: number;
}

export default function StudentPerformanceDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student] = useState({
    id: '1',
    name: 'Sarah Johnson',
    email: 'sarah.j@school.edu',
    avatar: 'bg-blue-500',
    class: 'Class 7A',
  });

  const [overallProgress] = useState(92);
  const [overallScore] = useState(92);

  const [activities, setActivities] = useState<ActivitySubmission[]>([
    {
      id: 'quiz-1',
      type: 'quiz',
      name: 'Multiple Choice Quiz',
      icon: CheckSquare,
      completed: true,
      autoGraded: true,
      score: 95,
      maxScore: 100,
      timeSpent: 180,
      submittedAt: new Date(2026, 1, 19, 10, 30),
      content: {
        answers: ['A', 'B', 'C', 'A'],
        correct: [true, true, false, true],
      },
    },
    {
      id: 'draw-1',
      type: 'draw',
      name: 'Draw-It: Photosynthesis Process',
      icon: PenTool,
      completed: true,
      autoGraded: false,
      score: 85,
      maxScore: 100,
      timeSpent: 300,
      submittedAt: new Date(2026, 1, 19, 11, 15),
      content: {
        imageUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400',
      },
      teacherComment: 'Great detail! Consider adding more labels.',
      manualScore: 85,
    },
    {
      id: 'open-1',
      type: 'open',
      name: 'Open-Ended: Explain Photosynthesis',
      icon: MessageSquare,
      completed: true,
      autoGraded: false,
      score: 92,
      maxScore: 100,
      timeSpent: 360,
      submittedAt: new Date(2026, 1, 19, 12, 0),
      content: {
        text: 'Photosynthesis is the process by which plants use sunlight, water, and carbon dioxide to create oxygen and energy in the form of sugar. This process occurs in the chloroplasts of plant cells and is essential for life on Earth.',
      },
      teacherComment: 'Excellent explanation! Very clear and concise.',
      manualScore: 92,
    },
    {
      id: 'matching-1',
      type: 'matching',
      name: 'Matching Pairs',
      icon: Target,
      completed: true,
      autoGraded: true,
      score: 100,
      maxScore: 100,
      timeSpent: 120,
      submittedAt: new Date(2026, 1, 19, 13, 0),
    },
    {
      id: 'climb-1',
      type: 'game',
      name: 'Time to Climb',
      icon: Gamepad2,
      completed: true,
      autoGraded: true,
      score: 90,
      maxScore: 100,
      timeSpent: 240,
      submittedAt: new Date(2026, 1, 19, 14, 0),
      content: {
        rank: 2,
        totalPlayers: 28,
      },
    },
    {
      id: 'word-cloud',
      type: 'wordcloud',
      name: 'Word Cloud Activity',
      icon: Cloud,
      completed: true,
      autoGraded: false,
      score: null,
      maxScore: 0,
      timeSpent: 90,
      submittedAt: new Date(2026, 1, 19, 15, 0),
      content: {
        words: ['Sunlight', 'Oxygen', 'Chlorophyll'],
      },
    },
  ]);

  const [editingActivity, setEditingActivity] = useState<string | null>(null);
  const [tempScore, setTempScore] = useState<number>(0);
  const [tempComment, setTempComment] = useState<string>('');

  const handleEditGrade = (activity: ActivitySubmission) => {
    setEditingActivity(activity.id);
    setTempScore(activity.manualScore || activity.score || 0);
    setTempComment(activity.teacherComment || '');
  };

  const handleSaveGrade = (activityId: string) => {
    setActivities(activities.map(a => 
      a.id === activityId 
        ? { ...a, score: tempScore, manualScore: tempScore, teacherComment: tempComment }
        : a
    ));
    setEditingActivity(null);
    toast.success('Grade updated successfully!');
  };

  const handleCancelEdit = () => {
    setEditingActivity(null);
    setTempScore(0);
    setTempComment('');
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-emerald-600';
    if (score >= 70) return 'text-orange-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const renderActivityContent = (activity: ActivitySubmission) => {
    switch (activity.type) {
      case 'draw':
        return (
          <div className="space-y-3 sm:space-y-4">
            <img
              src={activity.content.imageUrl}
              alt="Student drawing"
              className="w-full max-w-sm rounded-xl border-2 border-border"
            />
          </div>
        );
      
      case 'open':
        return (
          <div className="bg-muted/50 rounded-xl p-3 sm:p-4 border border-border">
            <p className="text-xs sm:text-sm text-foreground leading-relaxed break-words">{activity.content.text}</p>
          </div>
        );
      
      case 'quiz':
        return (
          <div className="space-y-2">
            {activity.content.answers.map((answer: string, idx: number) => (
              <div key={idx} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/50">
                {activity.content.correct[idx] ? (
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                ) : (
                  <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-red-600 flex-shrink-0 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-600 rounded-full" />
                  </div>
                )}
                <span className="text-xs sm:text-sm break-words">Question {idx + 1}: <span className="font-semibold">{answer}</span></span>
              </div>
            ))}
          </div>
        );
      
      case 'game':
        return (
          <div className="bg-gradient-to-br from-orange/10 to-primary/10 rounded-xl p-4 sm:p-6 border border-border">
            <div className="flex items-center gap-3 sm:gap-4">
              <Trophy className="w-8 h-8 sm:w-12 sm:h-12 text-orange flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-base sm:text-lg font-semibold break-words">Rank: #{activity.content.rank}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">out of {activity.content.totalPlayers} students</p>
              </div>
            </div>
          </div>
        );
      
      case 'wordcloud':
        return (
          <div className="flex flex-wrap gap-2">
            {activity.content.words.map((word: string, idx: number) => (
              <Badge key={idx} className="bg-primary/10 text-primary border-primary/20 px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                {word}
              </Badge>
            ))}
          </div>
        );
      
      default:
        return (
          <p className="text-sm text-muted-foreground">Auto-graded activity - no content preview</p>
        );
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      {/* Student Info Header */}
      <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
        {/* Action Buttons */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/gradebook')}
              className="rounded-xl text-xs sm:text-sm p-2 sm:px-4"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Back to Gradebook</span>
              <span className="sm:hidden">Back</span>
            </Button>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <Button variant="outline" className="rounded-xl text-xs sm:text-sm p-2 sm:px-4 flex-1 sm:flex-none">
                <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Export Report</span>
              </Button>
              <Button variant="outline" className="rounded-xl text-xs sm:text-sm p-2 sm:px-4 flex-1 sm:flex-none">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Share</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Student Info */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full ${student.avatar} flex-shrink-0 flex items-center justify-center text-white text-lg sm:text-2xl font-semibold`}>
            {student.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-semibold text-foreground break-words">{student.name}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">{student.email}</p>
            <Badge className="mt-1 sm:mt-2 rounded-lg text-xs">{student.class}</Badge>
          </div>
        </div>

        {/* Overall Progress */}
        <div className="border-t border-border pt-4 sm:pt-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs sm:text-sm font-medium text-foreground">Overall Progress</p>
            <p className="text-xs sm:text-sm font-semibold text-foreground">{overallProgress}%</p>
          </div>
          <Progress value={overallProgress} className="h-2" />
        </div>
      </div>

      {/* Activities List */}
      <div className="space-y-3 sm:space-y-4">
        <h2 className="text-base sm:text-xl font-semibold text-foreground mb-3 sm:mb-4 px-1">Activity Submissions</h2>
        
        {activities.map((activity) => (
          <div key={activity.id} className="bg-card border border-border rounded-2xl overflow-hidden">
            {/* Activity Header */}
            <div className="p-3 sm:p-4 lg:p-6 border-b border-border bg-muted/30">
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex-shrink-0 flex items-center justify-center">
                  <activity.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm sm:text-base text-foreground break-words">{activity.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2 text-xs sm:text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span>{Math.floor(activity.timeSpent / 60)}m {activity.timeSpent % 60}s</span>
                    </div>
                    <span className="hidden sm:inline">{activity.submittedAt.toLocaleDateString()}</span>
                    <span className="sm:hidden">{activity.submittedAt.toLocaleDateString()}</span>
                    {activity.autoGraded && (
                      <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                        Auto-graded
                      </Badge>
                    )}
                  </div>
                </div>
                
                {activity.score !== null ? (
                  <div className="text-right flex-shrink-0">
                    <p className={`text-2xl sm:text-3xl font-bold ${getScoreColor(activity.score)}`}>
                      {activity.score}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">/ {activity.maxScore}</p>
                  </div>
                ) : (
                  <Badge className="bg-orange-100 text-orange-700 border-orange-200 flex-shrink-0 text-xs">
                    Not Graded
                  </Badge>
                )}
              </div>
            </div>

            {/* Activity Content */}
            <div className="p-3 sm:p-4 lg:p-6">
              <div className="mb-4 sm:mb-6">
                <h4 className="text-xs sm:text-sm font-semibold text-foreground mb-2 sm:mb-3">Submission</h4>
                {renderActivityContent(activity)}
              </div>

              {/* Grading Section */}
              {!activity.autoGraded && (
                <div className="border-t border-border pt-4 sm:pt-6 space-y-3 sm:space-y-4">
                  {editingActivity === activity.id ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <Label htmlFor={`score-${activity.id}`} className="text-xs sm:text-sm">Score (0-{activity.maxScore})</Label>
                          <Input
                            id={`score-${activity.id}`}
                            type="number"
                            min="0"
                            max={activity.maxScore}
                            value={tempScore}
                            onChange={(e) => setTempScore(Number(e.target.value))}
                            className="rounded-xl mt-2 text-xs sm:text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor={`comment-${activity.id}`} className="text-xs sm:text-sm">Teacher Comment</Label>
                        <Textarea
                          id={`comment-${activity.id}`}
                          value={tempComment}
                          onChange={(e) => setTempComment(e.target.value)}
                          placeholder="Add feedback for the student..."
                          className="rounded-xl mt-2 min-h-[100px] text-xs sm:text-sm"
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <Button
                          onClick={() => handleSaveGrade(activity.id)}
                          className="rounded-xl text-xs sm:text-sm p-2 sm:px-4"
                        >
                          <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          Save Grade
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleCancelEdit}
                          className="rounded-xl text-xs sm:text-sm p-2 sm:px-4"
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      {activity.teacherComment && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
                          <p className="text-xs sm:text-sm font-semibold text-blue-900 mb-1">Teacher Feedback</p>
                          <p className="text-xs sm:text-sm text-blue-800 break-words">{activity.teacherComment}</p>
                        </div>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => handleEditGrade(activity)}
                        className="rounded-xl text-xs sm:text-sm p-2 sm:px-4"
                      >
                        <Edit3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        {activity.score !== null ? 'Edit Grade' : 'Add Grade'}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {activity.autoGraded && activity.teacherComment && (
                <div className="border-t border-border pt-4 sm:pt-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
                    <p className="text-xs sm:text-sm font-semibold text-blue-900 mb-1">Teacher Note</p>
                    <p className="text-xs sm:text-sm text-blue-800 break-words">{activity.teacherComment}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}